/**
 * The portal reads and the Filing status screen, in sentences (D-11, D-12, D-13) — pure, so
 * `tests/portal-fetch.test.ts` can hold them.
 *
 * The service decides everything: whether the lane is on, what each call came to, which one
 * action a row offers. This turns its answers into what a practitioner reads — never a kind
 * slug, a reference id or an ISO timestamp. Nothing here has run against GSTN; with the lane
 * off every control that would call the portal says so in one sentence.
 */

import type { FilingLane, PortalFetch } from './api'
import { istWords } from './filingLane'

/** The sentence a portal control shows when the lane is off, or null when it is on. */
export function laneOff(lane: Pick<FilingLane, 'mode' | 'sentence'> | null | undefined): string | null {
  if (!lane) return null
  return lane.mode === 'off' ? (lane.sentence ?? 'Filing through the GST portal is switched off.') : null
}

/** One fetch row as a line: what, when, and what it came to. */
export function fetchLine(row: Pick<PortalFetch, 'what' | 'at' | 'state_words' | 'sentence'>): string {
  const when = istWords(row.at)
  const head = when ? `${row.what}, at ${when}` : row.what
  return `${head} — ${row.state_words}. ${row.sentence}`.trim()
}

/** `07-2026` → `2026-27`, the financial year the month falls in; a quarter's own year. */
export function financialYearOf(period: string): string | null {
  const q = /^Q[1-4]-(\d{4}-\d{2})$/.exec(period)
  if (q) return q[1] ?? null
  const m = /^(\d{2})-(\d{4})$/.exec(period)
  if (!m) return null
  const month = Number(m[1])
  const year = Number(m[2])
  const start = month >= 4 ? year : year - 1
  return `${start}-${String(start + 1).slice(2)}`
}

export const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'] as const

/** What the portal's IMS shows for a worklist row, or null when IMS was never read. */
export function portalImsWords(row: { portal_ims_words?: string | null }): string | null {
  return row.portal_ims_words ? `The portal’s IMS: ${row.portal_ims_words}` : null
}

/** Whether a worklist row has something on the portal's IMS that a reset would change. */
export function canResetOnPortal(row: { portal_ims?: string | null }): boolean {
  return !!row.portal_ims && row.portal_ims !== 'no_action'
}
